import { create } from 'zustand';
import type { Tables } from '../lib/supabase/types';

type Chat = Tables<'chats'>;
// Extend ChatMessage with new fields (temporary until types are regenerated)
type ChatMessage = Tables<'chats_messages'> & {
  is_pinned?: boolean;
  reply_to_message_id?: number | null;
  chats_messages_reactions?: any[]
};

interface ChatState {
  chats: Chat[];
  activeChat: string | null;
  messages: Record<string, ChatMessage[]>;
  unreadChatIds: Set<string>;
  isLoading: boolean;

  // Chat panel state
  isPanelOpen: boolean;
  activeChatUserId: string | null;
  activeChatUserName: string | null;
  activeChatUserImage: string | null;
  activeChatPartnerOrgName: string | null;
  activeChatPartnerOrgLogo: string | null;

  // Typing indicators
  typingUsers: Record<string, string[]>; // chatId -> userIds[]

  // Actions
  setChats: (chats: Chat[]) => void;
  setActiveChat: (chatId: string | null) => void;
  setMessages: (chatId: string, messages: ChatMessage[]) => void;
  addMessage: (chatId: string, message: ChatMessage) => void;
  markChatAsRead: (chatId: string) => void;
  setUnreadChats: (chatIds: string[]) => void;
  setLoading: (loading: boolean) => void;

  // Panel actions
  openChatPanel: (chatId: string, userId: string, userName: string, userImage: string | null, partnerOrgName?: string | null, partnerOrgLogo?: string | null) => void;
  closeChatPanel: () => void;
  setTypingUsers: (chatId: string, userIds: string[]) => void;

  // Reply functionality
  replyingTo: ChatMessage | null;
  setReplyingTo: (message: ChatMessage | null) => void;

  // Edit functionality
  editingMessage: ChatMessage | null;
  setEditingMessage: (message: ChatMessage | null) => void;

  reset: () => void;
}


const initialState = {
  chats: [],
  activeChat: null,
  messages: {},
  unreadChatIds: new Set<string>(),
  isLoading: false,
  isPanelOpen: false,
  activeChatUserId: null,
  activeChatUserName: null,
  activeChatUserImage: null,
  activeChatPartnerOrgName: null,
  activeChatPartnerOrgLogo: null,
  typingUsers: {},
  replyingTo: null,
  editingMessage: null,
};


export const useChatStore = create<ChatState>((set) => ({
  ...initialState,

  setChats: (chats) => set({ chats }),

  setActiveChat: (activeChat) => set({ activeChat }),

  setMessages: (chatId, messages) => set((state) => ({
    messages: { ...state.messages, [chatId]: messages },
  })),

  addMessage: (chatId, message) => set((state) => ({
    messages: {
      ...state.messages,
      [chatId]: [...(state.messages[chatId] || []), message],
    },
  })),

  markChatAsRead: (chatId) => set((state) => {
    const newUnreadChatIds = new Set(state.unreadChatIds);
    newUnreadChatIds.delete(chatId);
    return { unreadChatIds: newUnreadChatIds };
  }),

  setUnreadChats: (chatIds) => set({ unreadChatIds: new Set(chatIds) }),

  setLoading: (isLoading) => set({ isLoading }),

  // Panel actions
  openChatPanel: (chatId, userId, userName, userImage, partnerOrgName = null, partnerOrgLogo = null) => set({
    activeChat: chatId,
    isPanelOpen: true,
    activeChatUserId: userId,
    activeChatUserName: userName,
    activeChatUserImage: userImage,
    activeChatPartnerOrgName: partnerOrgName,
    activeChatPartnerOrgLogo: partnerOrgLogo,
  }),

  closeChatPanel: () => set({
    isPanelOpen: false,
    activeChat: null,
    activeChatUserId: null,
    activeChatUserName: null,
    activeChatUserImage: null,
    activeChatPartnerOrgName: null,
    activeChatPartnerOrgLogo: null,
  }),

  setTypingUsers: (chatId, userIds) => set((state) => ({
    typingUsers: {
      ...state.typingUsers,
      [chatId]: userIds,
    },
  })),

  // Reply functionality
  replyingTo: null,
  setReplyingTo: (message) => set({ replyingTo: message }),

  // Edit functionality
  editingMessage: null,
  setEditingMessage: (message) => set({ editingMessage: message }),

  reset: () => set(initialState),
}));

